import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { StatusBadge } from '../components/coreComponents/badge';
import { Button } from '../components/coreComponents/button';
import { MainWrapper, MainWrapperHeader } from '../components/mainPageWrapper';

export function RecoveryDetail() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);

  const { data, loading, refetch } = useQuery(query, {
    variables: { accountId: accountId ?? '' },
    skip: accountId == null,
    fetchPolicy: 'cache-and-network'
  });

  // Must run before the early returns below so hook order stays stable
  // across renders. Passing '' when accountId is missing makes the
  // filter inside the hook trivially return false — no extra request
  // shape to handle.
  const hasPendingPurge = useAccountPurgePending(accountId ?? '');

  if (accountId == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Recovery" onBack={() => navigate('/recovery')} />
        <Typography>Account ID is required.</Typography>
      </MainWrapper>
    );
  }

  if (loading && data == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Recovery" onBack={() => navigate('/recovery')} />
        <Typography variant="body2">Loading…</Typography>
      </MainWrapper>
    );
  }

  const row = data?.deletedAccountOne ?? null;
  if (row == null) {
    return (
      <MainWrapper>
        <MainWrapperHeader title="Not found" onBack={() => navigate('/recovery')} />
      </MainWrapper>
    );
  }

  const canRestore = row.status === 'available' || row.status === 'failed';
  const isExpired = row.status === 'expired';
  // Server decides. `row.canPurge` already composes the cooling-off
  // period since deletedAt + the status-based rules; we only use
  // `row.purgeAvailableAt` here to render the "available after <date>"
  // explanation when the gate is the cooldown rather than status.
  const { canPurge } = row;
  const purgeAvailableAt = new Date(row.purgeAvailableAt);
  const waitingOnCooldown = !canPurge && !isExpired && row.status !== 'restoring';

  return (
    <MainWrapper>
      <MainWrapperHeader
        title={row.alias ?? `Account ${row.accountId}`}
        description="Deleted account"
        onBack={() => navigate('/recovery')}
        showIds
        id={row.accountId}
        primaryAction={
          <Button variant="secondary" onClick={() => void refetch()}>
            Refresh
          </Button>
        }
      />

      <Stack spacing={4} sx={{ maxWidth: 720 }}>
        {row.status === 'restoring' && row.restoreJobId != null && (
          <Alert severity="info" action={<ResetStuckRowButton accountId={row.accountId} />}>
            Restore in progress.{' '}
            <Box
              component="a"
              onClick={() => navigate(`/jobs/${row.restoreJobId}`)}
              sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Open job
            </Box>{' '}
            for live progress and the temporary password. If the row stays stuck after the job
            finishes, use Reset.
          </Alert>
        )}

        {row.status === 'restored' && (
          <Alert severity="success">
            Account already restored on {new Date(row.restoredAt!).toLocaleString()}.
          </Alert>
        )}

        {isExpired && (
          <Alert severity="warning">
            S3 dump expired on {new Date(row.expiresAt).toLocaleString()} (180-day retention). The
            backup is gone — this account can no longer be restored.
          </Alert>
        )}

        {row.status === 'failed' && row.errorMessage != null && (
          <Alert severity="error" sx={{ overflowWrap: 'anywhere' }}>
            Previous restore failed: {row.errorMessage}
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '180px 1fr',
            rowGap: 2,
            columnGap: 3
          }}
        >
          <Label>Status</Label>
          <Value>
            <StatusChip status={row.status} />
          </Value>

          <Label>Account ID</Label>
          <Value monospace>{row.accountId}</Value>

          <Label>Alias</Label>
          <Value subtle={row.alias == null}>{row.alias ?? '—'}</Value>

          <Label>Owner email</Label>
          <Value>{row.ownerEmail}</Value>

          <Label>Deleted at</Label>
          <Value>{new Date(row.deletedAt).toLocaleString()}</Value>

          <Label>Expires at</Label>
          <Value subtle={isExpired}>
            {new Date(row.expiresAt).toLocaleString()}
            {isExpired ? ' (expired)' : ''}
          </Value>

          <Label>S3 dump</Label>
          <Value monospace>{row.s3Key}</Value>

          <Label>Schema version</Label>
          <Value monospace={row.schemaVersion != null} subtle={row.schemaVersion == null}>
            <Stack spacing={1} alignItems="flex-start">
              <span>{row.schemaVersion ?? 'unknown'}</span>
              <SchemaDriftChip snapshot={row.schemaVersion} current={row.currentSchemaVersion} />
            </Stack>
          </Value>

          <Label>Current schema</Label>
          <Value monospace>{row.currentSchemaVersion}</Value>

          <Label>Delete job</Label>
          <Value monospace>{row.deleteJobId}</Value>

          {row.restoreJobId != null && (
            <>
              <Label>Restore job</Label>
              <Value monospace>{row.restoreJobId}</Value>
            </>
          )}

          {row.restoredAt != null && (
            <>
              <Label>Restored at</Label>
              <Value>{new Date(row.restoredAt).toLocaleString()}</Value>
            </>
          )}
        </Box>

        <PendingPurgeBanner accountId={row.accountId} />

        {!hasPendingPurge && (
          <Stack direction="row" spacing={3} alignItems="center">
            <Button variant="primary" disabled={!canRestore} onClick={() => setConfirmOpen(true)}>
              Restore account
            </Button>
            <Button variant="destructive" disabled={!canPurge} onClick={() => setPurgeOpen(true)}>
              Permanently delete backup
            </Button>
            {waitingOnCooldown && (
              <Typography variant="body2" color="text.secondary">
                Permanent purge available after {purgeAvailableAt.toLocaleString()} (cooling-off
                period since soft-delete).
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      <RestoreConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        accountId={row.accountId}
        alias={row.alias ?? null}
        ownerEmail={row.ownerEmail}
      />

      <PurgeBackupDialog
        open={purgeOpen}
        onClose={() => setPurgeOpen(false)}
        accountId={row.accountId}
        alias={row.alias ?? null}
        ownerEmail={row.ownerEmail}
      />
    </MainWrapper>
  );
}

interface PurgeBackupDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly accountId: string;
  readonly alias: string | null;
  readonly ownerEmail: string;
}

// Step 2 entry point. Mirrors the accountDetail soft-delete dialog —
// fires accountDeleteRequest(kind: accountPurgeBackup) and waits for a
// recipient to confirm via email. No type-id-to-confirm here because the
// email itself is the safety gate.
function PurgeBackupDialog(props: PurgeBackupDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [request, { loading }] = useMutation(purgeRequestMutation, {
    refetchQueries: ['RecoveryPending', 'PendingAdminActionMany']
  });

  const handleClose = () => {
    if (loading) return;
    setErrorMessage(null);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
        Request permanent backup purge
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            A confirmation email will be sent to the admin recipient list. Nothing is destroyed
            until someone clicks the link in that email and confirms. After confirmation the S3 dump
            for{' '}
            <Box component="span" sx={{ fontFamily: 'monospace' }}>
              {props.accountId}
            </Box>{' '}
            (owner {props.ownerEmail}) is deleted and the Recovery row is removed — the account
            becomes permanently unrecoverable.
          </DialogContentText>

          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="destructive"
          disabled={loading}
          onClick={async () => {
            setErrorMessage(null);
            try {
              await request({
                variables: { accountId: props.accountId, kind: 'accountPurgeBackup' }
              });
              props.onClose();
            } catch (err: any) {
              setErrorMessage(err.message ?? 'Failed to send purge request');
            }
          }}
        >
          {loading ? 'Sending…' : 'Send confirmation email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Used by RecoveryDetail to hide Restore + Permanently-delete-backup
// while a purge request is awaiting email confirmation. Shares the
// recoveryPendingQuery cache with PendingPurgeBanner so this doesn't
// add a second network round-trip.
function useAccountPurgePending(accountId: string): boolean {
  const { data } = useQuery(recoveryPendingQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });
  return (
    data?.pendingAdminActionMany.items.some(
      item => item.accountId === accountId && item.kind === 'accountPurgeBackup'
    ) ?? false
  );
}

function PendingPurgeBanner({ accountId }: { readonly accountId: string }) {
  const apollo = useApolloClient();
  const { data, refetch } = useQuery(recoveryPendingQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000
  });

  const [cancelRequest, { loading: cancelling }] = useMutation(purgeCancelMutation);
  const [resendRequest, { loading: resending }] = useMutation(purgeResendMutation);

  const pending = data?.pendingAdminActionMany.items.find(
    item => item.accountId === accountId && item.kind === 'accountPurgeBackup'
  );

  const wasPending = useRef(false);
  useEffect(() => {
    if (pending != null) {
      wasPending.current = true;
      return;
    }
    if (wasPending.current) {
      wasPending.current = false;
      apollo.refetchQueries({ include: ['DeletedAccountOne'] });
    }
  }, [pending, apollo]);

  if (pending == null) return null;

  return (
    <Alert severity="warning" sx={{ maxWidth: 720 }}>
      <Stack spacing={1.5}>
        <Typography variant="body2">
          Permanent backup purge awaiting confirmation. Initiated by{' '}
          <strong>{pending.initiatedByEmail}</strong> on{' '}
          {new Date(pending.createdAt).toLocaleString()}. Link expires{' '}
          {new Date(pending.expiresAt).toLocaleString()}.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="sm"
            variant="secondary"
            disabled={cancelling || resending}
            onClick={async () => {
              await cancelRequest({ variables: { id: pending.id } });
              await refetch();
            }}
          >
            {cancelling ? 'Cancelling…' : 'Cancel request'}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={cancelling || resending}
            onClick={async () => {
              await resendRequest({ variables: { id: pending.id } });
              await refetch();
            }}
          >
            {resending ? 'Resending…' : 'Resend email'}
          </Button>
        </Stack>
      </Stack>
    </Alert>
  );
}

function ResetStuckRowButton({ accountId }: { readonly accountId: string }) {
  const [reset, { loading }] = useMutation(resetMutation, {
    variables: { accountId },
    refetchQueries: ['DeletedAccountOne']
  });
  return (
    <Button
      variant="secondary"
      disabled={loading}
      onClick={async () => {
        try {
          await reset();
        } catch (err: any) {
          // Surface as alert — the backend rejects if the linked job is
          // still pending/processing, so this isn't expected on a stuck row.
          alert(err.message ?? 'Failed to reset row.');
        }
      }}
    >
      {loading ? 'Resetting…' : 'Reset'}
    </Button>
  );
}

function RestoreConfirmDialog(props: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly accountId: string;
  readonly alias: string | null;
  readonly ownerEmail: string;
}) {
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [restore, { loading }] = useMutation(restoreMutation);

  const matches = confirmation.trim() === props.accountId;

  const handleClose = () => {
    if (loading) return;
    setConfirmation('');
    setErrorMessage(null);
    props.onClose();
  };

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: 'text.secondary', fontWeight: 600 }}>Restore account</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <DialogContentText sx={{ fontSize: '0.875rem' }}>
            Recreates the global account record, the per-tenant MySQL database (from the S3 dump),
            and the Secrets Manager secret. Then runs <code>prisma migrate deploy</code> to roll the
            schema forward to the current HEAD — if any migration fails on the old data, the restore
            is marked failed and devs can review the CodeBuild logs. The owner ({props.ownerEmail})
            is re-provisioned with a new temporary password - share it with them after this
            completes. Other users keep their citadel records but need credentials re-issued
            one-by-one from the user overview tab.
          </DialogContentText>

          <Box>
            <Typography color="text.secondary" variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Type{' '}
              <Box
                component="span"
                sx={{ fontFamily: 'monospace', color: 'primary.main', fontWeight: 600 }}
              >
                {props.accountId}
              </Box>{' '}
              to confirm
            </Typography>
            <TextField
              fullWidth
              placeholder={props.accountId}
              value={confirmation}
              onChange={event => setConfirmation(event.target.value)}
              disabled={loading}
              autoFocus
              error={confirmation.length > 0 && !matches}
              size="small"
              inputProps={{ style: { fontFamily: 'monospace' } }}
            />
          </Box>
          {errorMessage != null && <Alert severity="error">{errorMessage}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!matches || loading}
          onClick={async () => {
            setErrorMessage(null);
            try {
              const result = await restore({ variables: { accountId: props.accountId } });
              const jobId = result.data?.accountRestore.jobId;
              if (jobId != null) navigate(`/jobs/${jobId}`);
            } catch (err: any) {
              setErrorMessage(err.message ?? 'Failed to enqueue restore');
            }
          }}
        >
          {loading ? 'Starting…' : 'Restore account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Label({ children }: { readonly children: React.ReactNode }) {
  return (
    <Typography variant="labelSmall" sx={{ color: 'text.secondary' }}>
      {children}
    </Typography>
  );
}

interface ValueProps {
  readonly children: React.ReactNode;
  readonly monospace?: boolean;
  readonly subtle?: boolean;
}

function Value({ children, monospace, subtle }: ValueProps) {
  return (
    <Typography
      variant="body2"
      // Rendered as a div (not the default <p>) because some values contain
      // block-level children (e.g. the schema row's Stack + Chip), which are
      // invalid DOM nesting inside a <p>.
      component="div"
      sx={{
        fontFamily: monospace === true ? 'monospace' : undefined,
        color: subtle === true ? 'text.secondary' : undefined,
        overflowWrap: 'anywhere'
      }}
    >
      {children}
    </Typography>
  );
}

function SchemaDriftChip({
  snapshot,
  current
}: {
  readonly snapshot: string | null | undefined;
  readonly current: string;
}) {
  if (snapshot == null) {
    return <StatusBadge tone="warning" label="unknown snapshot" />;
  }
  if (snapshot === current) {
    return <StatusBadge tone="success" label="up-to-date" />;
  }
  return <StatusBadge tone="warning" label="drift detected" />;
}

function StatusChip({ status }: { readonly status: string }) {
  if (status === 'expired') {
    return <StatusBadge tone="error" label="expired — not recoverable" />;
  }
  const color =
    status === 'restored'
      ? 'success'
      : status === 'failed'
        ? 'error'
        : status === 'restoring'
          ? 'info'
          : status === 'available'
            ? 'info'
            : 'default';
  return <StatusBadge tone={color} label={status} />;
}

const query = gql<DeletedAccountOneGQL>`
  query DeletedAccountOne($accountId: ID!) {
    deletedAccountOne(accountId: $accountId) {
      accountId
      alias
      ownerEmail
      s3Key
      deletedAt
      deleteJobId
      restoreJobId
      restoredAt
      status
      errorMessage
      schemaVersion
      currentSchemaVersion
      expiresAt
      canPurge
      purgeAvailableAt
    }
  }
`;

const restoreMutation = gql<AccountRestoreGQL>`
  mutation AccountRestore($accountId: ID!) {
    accountRestore(accountId: $accountId) {
      jobId
      temporaryPassword
      linkedExistingUser
    }
  }
`;

const resetMutation = gql<DeletedAccountResetGQL>`
  mutation DeletedAccountReset($accountId: ID!) {
    deletedAccountReset(accountId: $accountId) {
      accountId
      status
    }
  }
`;

const purgeRequestMutation = gql<RecoveryPurgeRequestGQL>`
  mutation RecoveryPurgeRequest($accountId: ID!, $kind: PendingAdminActionKind!) {
    accountDeleteRequest(accountId: $accountId, kind: $kind) {
      id
      status
      expiresAt
    }
  }
`;

const recoveryPendingQuery = gql<RecoveryPendingGQL>`
  query RecoveryPending {
    pendingAdminActionMany {
      items {
        id
        kind
        accountId
        initiatedByEmail
        createdAt
        expiresAt
        status
      }
    }
  }
`;

const purgeCancelMutation = gql<RecoveryPurgeCancelGQL>`
  mutation RecoveryPurgeCancel($id: ID!) {
    accountDeleteRequestCancel(id: $id) {
      id
      status
    }
  }
`;

const purgeResendMutation = gql<RecoveryPurgeResendGQL>`
  mutation RecoveryPurgeResend($id: ID!) {
    accountDeleteRequestResend(id: $id) {
      id
      expiresAt
      status
    }
  }
`;
